import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  AnimatePresence,
} from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { CONTACT_LINKS, PORTAL_LINKS } from "@/lib/site-data";

/**
 * Scroll-controlled, full-bleed video hero.
 *
 * Desktop (and motion-safe): A 280vh "scroll rig" wraps a sticky 100vh stage.
 * The user's scroll position drives videoRef.currentTime via rAF lerp for smoothness.
 * Three text states fade/slide in at progress milestones (0.04, 0.38, 0.72).
 *
 * Mobile / reduced-motion: Bypass the rig entirely. Render a 100vh hero with an
 * autoplay muted loop video and the first text state already visible.
 */

const HERO_STATES = [
  {
    eyebrow: "Care, in the chat you already use",
    title: "The doctor is",
    title_em: "one message away.",
    body:
      "Talk to licensed Nigerian clinicians on WhatsApp, voice or video  backed by an AI Care Companion that listens, triages and never sleeps.",
  },
  {
    eyebrow: "Built for Nigeria",
    title: "From cough to chronic care,",
    title_em: "we’ve got you.",
    body:
      "Routine check-ups, mental health, prescriptions and follow-ups  handled with the warmth of a family doctor and the speed of a chat.",
  },
  {
    eyebrow: "Care that follows you",
    title: "Your records, your team,",
    title_em: "in your pocket.",
    body:
      "Every visit, prescription and lab result is saved to a single private record  secure, portable and ready for the next clinician.",
  },
];

function useIsMobileOrReduced() {
  const [bypass, setBypass] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqlReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mqlMobile = window.matchMedia("(max-width: 767px)");
    const update = () => setBypass(mqlReduced.matches || mqlMobile.matches);
    update();
    mqlReduced.addEventListener?.("change", update);
    mqlMobile.addEventListener?.("change", update);
    return () => {
      mqlReduced.removeEventListener?.("change", update);
      mqlMobile.removeEventListener?.("change", update);
    };
  }, []);
  return bypass;
}

const ScrollRigHero = () => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const targetTimeRef = useRef(0);
  const canSeekRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoReady, setVideoReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ["start start", "end end"],
  });

  // For nav/scrim contrast as we scroll
  const scrim = useTransform(scrollYProgress, [0, 1], [0.4, 0.62]);

  // Update the active text index based on milestones
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    let idx = 0;
    if (v > 0.66) idx = 2;
    else if (v > 0.34) idx = 1;
    setActiveIndex((prev) => (prev !== idx ? idx : prev));

    if (duration > 0 && videoRef.current) {
      // Map progress to currentTime; clamp to [0, duration - 0.05]
      targetTimeRef.current = Math.max(
        0,
        Math.min(duration - 0.05, v * duration)
      );
    }
  });

  // Wait until video is ready to seek smoothly. Some browsers/headless
  // chrome won't decode a paused video unless we kick it with play()->pause().
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let cancelled = false;
    const markReady = () => {
      if (cancelled) return;
      canSeekRef.current = true;
      setVideoReady(true);
      try {
        if (v.currentTime < 0.05) v.currentTime = 0.05;
      } catch (_) {
        /* ignore */
      }
    };
    const onLoadedData = () => {
      // Force a frame to render: brief play/pause on muted video is allowed.
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          try {
            v.pause();
          } catch (_) {
            /* ignore */
          }
          markReady();
        }).catch(() => {
          // Autoplay rejected  still mark ready so poster can hide.
          markReady();
        });
      } else {
        markReady();
      }
    };
    v.addEventListener("loadeddata", onLoadedData, { once: true });
    v.addEventListener("canplay", markReady);
    // Safety timeout so the poster doesn't linger forever in degraded networks.
    const safety = setTimeout(markReady, 6000);
    return () => {
      cancelled = true;
      clearTimeout(safety);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("canplay", markReady);
    };
  }, []);

  // Smooth seek: lerp video.currentTime toward targetTime each frame
  useEffect(() => {
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (
        v &&
        canSeekRef.current &&
        !Number.isNaN(v.duration) &&
        v.duration > 0
      ) {
        const cur = v.currentTime;
        const target = targetTimeRef.current;
        const next = cur + (target - cur) * 0.18;
        if (Math.abs(next - cur) > 0.012) {
          try {
            v.currentTime = next;
          } catch (_) {
            /* ignore */
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section
      ref={wrapperRef}
      data-testid="hero-scroll-rig"
      className="relative w-full"
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-ink">
        <video
          ref={videoRef}
          data-testid="hero-video"
          muted
          playsInline
          preload="auto"
          // Don't autoplay  scroll drives the timeline.
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (!Number.isNaN(d)) setDuration(d);
          }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src="/media/hero.webm" type="video/webm" />
          <source src="/media/hero.mp4" type="video/mp4" />
        </video>

        {/* Poster fallback while video buffers */}
        {!videoReady && (
          <img
            src="https://images.pexels.com/photos/5452195/pexels-photo-5452195.jpeg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        <motion.div
          aria-hidden="true"
          className="absolute inset-0 hero-shade"
          style={{ opacity: scrim }}
        />

        {/* Top vignette to keep nav legible */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/55 to-transparent"
        />

        {/* Hero content */}
        <div className="relative h-full container-x flex items-end pb-20 md:pb-24 lg:pb-28">
          <div className="grid w-full grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 28, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p
                    className="mb-5 inline-flex items-center gap-2 rounded-full bg-cream/15 px-3.5 py-1.5 text-[12.5px] font-medium uppercase tracking-[0.16em] text-cream/95 backdrop-blur"
                    data-testid={`hero-eyebrow-${activeIndex}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-clay" />
                    {HERO_STATES[activeIndex].eyebrow}
                  </p>
                  <h1
                    className="font-display text-cream font-extrabold leading-[1.02] tracking-tight text-[44px] sm:text-[60px] lg:text-[84px] text-balance"
                    data-testid={`hero-title-${activeIndex}`}
                  >
                    {HERO_STATES[activeIndex].title}{" "}
                    <span className="block lg:inline text-clay">
                      {HERO_STATES[activeIndex].title_em}
                    </span>
                  </h1>
                  <p
                    className="mt-6 max-w-2xl text-[16.5px] sm:text-[18px] text-cream/90 leading-relaxed text-pretty"
                    data-testid={`hero-body-${activeIndex}`}
                  >
                    {HERO_STATES[activeIndex].body}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a
                  href={CONTACT_LINKS.whatsapp.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 rounded-full bg-cream text-ink px-6 py-3.5 text-[15px] font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  Start on WhatsApp
                  <MessageCircle className="h-4 w-4" />
                </a>
                <a
                  href={PORTAL_LINKS.patientSignup}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream px-6 py-3.5 text-[15px] font-semibold hover:bg-cream/10 transition-colors"
                >
                  Sign up on the web
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-4">
              <div className="ml-auto max-w-[300px] rounded-2xl bg-cream/10 backdrop-blur-md border border-cream/20 p-5 text-cream">
                <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-cream/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-clay animate-pulse" />
                  Live now
                </div>
                <p className="mt-2 font-display text-[28px] leading-[1.1] font-bold">
                  47 clinicians online
                </p>
                <p className="mt-1.5 text-[13.5px] text-cream/80">
                  Average response under 11 minutes
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator + progress dots */}
        <div className="pointer-events-none absolute inset-x-0 bottom-6 flex flex-col items-center gap-3 text-cream/85">
          <div className="flex items-center gap-1.5">
            {HERO_STATES.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === activeIndex ? "w-7 bg-cream" : "w-1.5 bg-cream/40"
                }`}
                data-testid={`hero-progress-dot-${i}`}
              />
            ))}
          </div>
          <p className="text-[11.5px] uppercase tracking-[0.22em]">Scroll</p>
        </div>
      </div>
    </section>
  );
};

const FallbackHero = () => (
  <section
    data-testid="hero-fallback"
    className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-ink"
  >
    <video
      data-testid="hero-fallback-video"
      poster="https://images.pexels.com/photos/5452195/pexels-photo-5452195.jpeg"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 h-full w-full object-cover"
    >
      <source src="/media/hero.webm" type="video/webm" />
      <source src="/media/hero.mp4" type="video/mp4" />
    </video>
    <div aria-hidden="true" className="absolute inset-0 hero-shade opacity-90" />
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/55 to-transparent"
    />
    <div className="relative h-full container-x flex items-end pb-16">
      <div>
        <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-cream/15 px-3.5 py-1.5 text-[12px] font-medium uppercase tracking-[0.16em] text-cream/95">
          <span className="h-1.5 w-1.5 rounded-full bg-clay" />
          Care, in the chat you already use
        </p>
        <h1 className="font-display text-cream font-extrabold leading-[1.04] tracking-tight text-[40px] sm:text-[56px] text-balance max-w-3xl">
          The doctor is{" "}
          <span className="text-clay">one message away.</span>
        </h1>
        <p className="mt-5 max-w-xl text-cream/90 text-[16px] leading-relaxed">
          Talk to licensed Nigerian clinicians on WhatsApp, voice or video  backed by an AI Care Companion that listens, triages and never sleeps.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a
            href={CONTACT_LINKS.whatsapp.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-fallback-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-cream text-ink px-5 py-3 text-[15px] font-semibold"
          >
            Start on WhatsApp
            <MessageCircle className="h-4 w-4" />
          </a>
          <a
            href={PORTAL_LINKS.patientSignup}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-fallback-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream px-5 py-3 text-[15px] font-semibold"
          >
            Sign up on the web
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default function HeroVideo() {
  const bypass = useIsMobileOrReduced();
  return bypass ? <FallbackHero /> : <ScrollRigHero />;
}
